import {Box, capitalize, FormControlLabel, IconButton} from "@mui/material";
import {FastField, Field} from "formik";
import {Switch, TextField} from 'formik-mui';
import {useUnteraufgabenHandling} from "../../hooks/unteraufgaben/useUnteraufgabenHandling";
import AntworttypSelect from "./AntworttypSelect";
import AbortCriterionFields from "./AbortCriterionFields";
import ErwartungswerteFields from "./ErwartungswerteFields";
import MultipleGuard from "../guards/MultipleGuard";
import {permissions} from "../../permissions";
import ButtonDeleteUnteraufgabe from "./ButtonDeleteUnteraufgabe";
import DeleteIcon from "@mui/icons-material/Delete";
import {Unteraufgabe} from "../../api";
import {unteraufgabenStyles} from "../../styles/unteraufgaben/unteraufgaben.styles";
import {DEFAULT_FOREIGN_LANGUAGE, DEFAULT_LANGUAGE} from "../../config/language";
import Grid from "@mui/material/Grid2";

interface FieldsContentUnteraufgabeProps {
    canEditFields: boolean;
    reihenfolge: number;
    unteraufgabe: Unteraufgabe;
    sprache?: string;
    remove: (index: number) => void;
}

export default function FieldsContentUnteraufgabe({
                                                      canEditFields,
                                                      reihenfolge,
                                                      unteraufgabe,
                                                      sprache,
                                                      remove
                                                  }: FieldsContentUnteraufgabeProps) {
    const { translation, translationLanguagesCodes } = useUnteraufgabenHandling();
    const languageCode = sprache || DEFAULT_FOREIGN_LANGUAGE;
    const isDefaultLanguage = (sprache ?? DEFAULT_LANGUAGE) === DEFAULT_LANGUAGE;
    return (
    <Grid
        container
        spacing={2}
        sx={unteraufgabenStyles.fieldsContentUnteraufgabe.container}
    >
        <Grid size={8}>
            <Box component="span" sx={unteraufgabenStyles.fieldsContentUnteraufgabe.subtasknumber}>
                {reihenfolge + 1}
            </Box>
        </Grid>

        <Grid size={4} sx={unteraufgabenStyles.fieldsContentUnteraufgabe.headerRight}>
            <FormControlLabel
                control={
                    <Field
                        component={Switch}
                        name={`unteraufgaben.${reihenfolge}.pflichtfeld`}
                        type="checkbox"
                        disabled={!canEditFields}
                    />
                }
                label={capitalize(translation("required-field"))}
                labelPlacement="end"
            />
            <FormControlLabel
                control={
                    <Field
                        component={Switch}
                        type="checkbox"
                        name={`unteraufgaben.${reihenfolge}.nurAuswertung`}
                        disabled={!canEditFields}
                    />
                }
                label={capitalize(translation("evaluation-only"))}
                labelPlacement="end"
            />
            <AbortCriterionFields
                reihenfolge={reihenfolge}
                canEditFields={canEditFields}
                variant="toggle"
            />
            <MultipleGuard
                permissions={[
                    permissions.unteraufgaben.delete,
                    permissions.unteraufgaben.own_delete,
                ]}
            >
                {!unteraufgabe.id.startsWith("neu") ? (
                    <ButtonDeleteUnteraufgabe
                        aufgabeId={unteraufgabe.aufgabeId}
                        unteraufgabeId={unteraufgabe.id}
                    />
                ) : (
                    <IconButton onClick={() => remove(reihenfolge)}>
                        <DeleteIcon />
                    </IconButton>
                )}
            </MultipleGuard>
        </Grid>

        {/* === Title / Description / Translations / Answer type / Weighting === */}
        <Grid size={3}>
            <FastField
                id={`unteraufgaben.${reihenfolge}.titel`}
                component={TextField}
                label={capitalize(translation("sub-task-attributes-title"))}
                name={`unteraufgaben.${reihenfolge}.titel`}
                required
                disabled={!canEditFields}
                fullWidth
            />
        </Grid>

        <Grid size={!isDefaultLanguage ? 3: 6} >
            <FastField
                id={`unteraufgaben.${reihenfolge}.beschreibung`}
                component={TextField}
                label={capitalize(translation("sub-task-attributes-description"))}
                name={`unteraufgaben.${reihenfolge}.beschreibung`}
                disabled={!canEditFields}
                fullWidth
            />
        </Grid>

        {!isDefaultLanguage && (
            <Grid size={3} >
                <FastField
                    id={`unteraufgaben.${reihenfolge}.foreignTitel`}
                    component={TextField}
                    label={
                        capitalize(translation("sub-task-attributes-title")) +
                        " (" +
                        translationLanguagesCodes(languageCode) +
                        ")"
                    }
                    name={`unteraufgaben.${reihenfolge}.foreignTitel`}
                    inputProps={{ maxLength: 255 }}
                    disabled={!canEditFields}
                    fullWidth
                />
            </Grid>
        )}

        <Grid size={2} >
            <Box sx={unteraufgabenStyles.fieldsContentUnteraufgabe.antworttypField}
            >
                <AntworttypSelect
                    id={`unteraufgaben.${reihenfolge}.antworttyp`}
                    name={`unteraufgaben.${reihenfolge}.antworttyp`}
                    label={capitalize(translation("sub-task-attributes-answer-type"))}
                    required
                    disabled={!canEditFields}
                />
            </Box>


        </Grid>

        <Grid size={1}>
            <FastField
                id={`unteraufgaben.${reihenfolge}.gewichtung`}
                component={TextField}
                type="number"
                inputProps={{ min: 0, step: 0.01, style: { textAlign: "right" } }}
                label={capitalize(translation("sub-task-attributes-weighting"))}
                name={`unteraufgaben.${reihenfolge}.gewichtung`}
                disabled={!canEditFields}
                fullWidth
            />
        </Grid>

        {/* Force new row */}
        <Grid size={12} />

        {/* === Abort on Answer Field === */}
        <AbortCriterionFields
            reihenfolge={reihenfolge}
            canEditFields={canEditFields}
            variant="field"
        />

        {/* === Expected Values === */}
        <ErwartungswerteFields
            reihenfolge={reihenfolge}
            antworttyp={unteraufgabe.antworttyp}
            canEditFields={canEditFields}
        />

        {/* === Prompts === */}
        <Grid size={ sprache === DEFAULT_LANGUAGE ? 12 : 6}>
            <FastField
                id={`unteraufgaben.${reihenfolge}.prompt`}
                component={TextField}
                label={capitalize(translation("sub-task-attributes-prompt"))}
                name={`unteraufgaben.${reihenfolge}.prompt`}
                required
                disabled={!canEditFields}
                multiline
                rows={5}
                fullWidth
                sx={
                    sprache === DEFAULT_LANGUAGE
                        ? unteraufgabenStyles.fieldsContentUnteraufgabe.promptField
                        : unteraufgabenStyles.fieldsContentUnteraufgabe.prompt2langField
                }
            />
        </Grid>

        {!isDefaultLanguage && (
            <Grid size={6}>
                <FastField
                    id={`unteraufgaben.${reihenfolge}.foreignPrompt`}
                    component={TextField}
                    label={
                        capitalize(translation("sub-task-attributes-prompt")) +
                        " (" +
                        translationLanguagesCodes(languageCode) +
                        ")"
                    }
                    name={`unteraufgaben.${reihenfolge}.foreignPrompt`}
                    required
                    disabled={!canEditFields}
                    multiline
                    rows={5}
                    fullWidth
                />
            </Grid>
        )}
    </Grid>
    );
}